// tabs/CampaignsTab.jsx
import React, { useEffect, useState } from "react";
import {
  FaBullhorn,
  FaPause,
  FaPlay,
  FaPlus,
  FaSync,
  FaVial,
} from "react-icons/fa";

import {
  getEmailCampaigns,
  getEmailCampaignsSummary,
  launchEmailCampaign,
  pauseEmailCampaign,
  prepareEmailCampaignRecipients,
} from "../../../../../services/mailingServices";

import { getCampaignStatusMeta, getProductLabel } from "../constants";

export default function CampaignsTab({
  campaignSeed,
  refreshKey = 0,
  onOpenCreateCampaign,
}) {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    byStatus: [],
    byProduct: [],
  });

  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      const [list, stats] = await Promise.all([
        getEmailCampaigns(),
        getEmailCampaignsSummary(),
      ]);

      setCampaigns(list || []);
      setSummary(stats || { total: 0, byStatus: [], byProduct: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line
  }, [refreshKey]);

  const prepareCampaign = async (id) => {
    await prepareEmailCampaignRecipients(id, { limit: 1000 });
    fetchCampaigns();
  };

  const launchCampaign = async (id) => {
    await launchEmailCampaign(id);
    fetchCampaigns();
  };

  const pauseCampaign = async (id) => {
    await pauseEmailCampaign(id);
    fetchCampaigns();
  };

  return (
    <div className="space-y-5">
      {campaignSeed && (
        <div className="rounded-3xl border border-blue-200 bg-blue-50 p-4 text-blue-950 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100">
          <div className="font-semibold">Source de campagne prête</div>
          <div className="mt-1 text-sm opacity-90">
            Source :{" "}
            {campaignSeed.mode === "selection"
              ? "sélection"
              : campaignSeed.mode === "filters"
                ? "filtres"
                : "manuelle"}{" "}
            {campaignSeed.selectedIds?.length
              ? `• ${campaignSeed.selectedIds.length} prospect(s) sélectionné(s)`
              : ""}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
        <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Campagnes email</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Préparez vos destinataires, envoyez un test puis lancez l’envoi
                progressif.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchCampaigns}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm"
              >
                <FaSync /> Actualiser
              </button>

              <button
                type="button"
                onClick={onOpenCreateCampaign}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-3 py-2 text-sm"
              >
                <FaPlus /> Créer
              </button>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-slate-500">Chargement…</div>
            ) : campaigns.length === 0 ? (
              <EmptyCampaigns onCreate={onOpenCreateCampaign} />
            ) : (
              campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onPrepare={() => prepareCampaign(campaign.id)}
                  onLaunch={() => launchCampaign(campaign.id)}
                  onPause={() => pauseCampaign(campaign.id)}
                />
              ))
            )}
          </div>
        </div>

        <CampaignSummaryCard summary={summary} />
      </div>
    </div>
  );
}

function CampaignCard({ campaign, onPrepare, onLaunch, onPause }) {
  const meta = getCampaignStatusMeta(campaign.status);

  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold truncate">{campaign.name}</h3>

            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs ${meta.className}`}
            >
              {meta.label}
            </span>
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {getProductLabel(campaign.product_key)} •{" "}
            {campaign.from_email || "—"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onPrepare}
            disabled={!["draft", "prepared", "paused"].includes(campaign.status)}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-50 text-blue-700 px-3 py-2 text-sm disabled:opacity-50 dark:bg-blue-950/40 dark:text-blue-200"
          >
            <FaVial /> Préparer
          </button>

          <button
            type="button"
            onClick={onLaunch}
            disabled={!["prepared", "paused"].includes(campaign.status)}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-3 py-2 text-sm disabled:opacity-50"
          >
            <FaPlay /> Lancer
          </button>

          <button
            type="button"
            onClick={onPause}
            disabled={campaign.status !== "running"}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-50 text-amber-700 px-3 py-2 text-sm disabled:opacity-50 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <FaPause /> Pause
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2">
        <MiniStat label="Cibles" value={campaign.total_targets} />
        <MiniStat label="File" value={campaign.queued_count} />
        <MiniStat label="Envoyés" value={campaign.sent_count} />
        <MiniStat label="Ouverts" value={campaign.opened_count} />
        <MiniStat label="Clics" value={campaign.clicked_count} />
        <MiniStat label="Erreurs" value={campaign.failed_count} />
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/70 px-3 py-2">
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="font-semibold tabular-nums">{value || 0}</div>
    </div>
  );
}

function CampaignSummaryCard({ summary }) {
  return (
    <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
      <div className="font-semibold">Synthèse</div>

      <div className="mt-4 rounded-2xl bg-slate-50 dark:bg-slate-800/70 p-4">
        <div className="text-xs text-slate-500">Total campagnes</div>
        <div className="mt-1 text-3xl font-bold">{summary?.total || 0}</div>
      </div>

      <div className="mt-4 text-sm space-y-2">
        {(summary?.byStatus || []).map((row) => (
          <div
            key={row.status}
            className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2"
          >
            <span>{getCampaignStatusMeta(row.status).label}</span>
            <span className="font-semibold">{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyCampaigns({ onCreate }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center">
      <FaBullhorn className="mx-auto text-3xl text-slate-400" />
      <div className="mt-3 font-semibold">Aucune campagne pour le moment</div>
      <p className="mt-1 text-sm text-slate-500">
        Créez une campagne, choisissez un modèle, préparez les destinataires et
        envoyez un test.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-4 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-2 text-sm"
      >
        Créer une campagne
      </button>
    </div>
  );
}